import React, { useMemo, useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Modern date picker.
 * value / onChange use "yyyy-MM-dd" strings to match existing form FormData.
 */
const DatePicker = ({
  id,
  value = "",
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  required = false,
  className,
  fromYear = 1990,
  toYear = new Date().getFullYear() + 1,
}) => {
  const [open, setOpen] = useState(false);

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [value]);

  const handleSelect = (date) => {
    if (!date) {
      onChange?.("");
      return;
    }
    onChange?.(format(date, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      {/* Keep a hidden input so native form required validation still works */}
      <input
        id={id}
        name="issueDate"
        type="text"
        value={value || ""}
        required={required}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        onChange={() => {}}
      />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id ? `${id}-trigger` : undefined}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full h-10 justify-start text-left font-normal rounded-xl px-3",
              "border-input bg-transparent hover:bg-accent/50",
              !selectedDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-purple-600 shrink-0" />
            {selectedDate ? (
              <span className="text-foreground">
                {format(selectedDate, "MMM d, yyyy")}
              </span>
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 rounded-xl overflow-hidden shadow-lg border"
          align="start"
        >
          <div className="bg-gradient-to-b from-purple-50/80 to-background dark:from-purple-950/30 dark:to-background px-3 pt-3 pb-1 border-b">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Select issue date
            </p>
            <p className="text-sm font-semibold text-foreground mt-0.5">
              {selectedDate
                ? format(selectedDate, "EEEE, MMM d, yyyy")
                : "No date selected"}
            </p>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            defaultMonth={selectedDate || new Date()}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            disabled={{ after: new Date(toYear, 11, 31) }}
            initialFocus
            classNames={{
              day_selected:
                "bg-purple-600 text-white hover:bg-purple-700 hover:text-white focus:bg-purple-600 focus:text-white",
              day_today: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
            }}
          />
          <div className="flex items-center justify-between gap-2 p-3 border-t bg-muted/30">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => {
                onChange?.("");
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                handleSelect(new Date());
              }}
            >
              Today
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePicker;
